/* LoginDark — Dark AI-native sign-in / sign-up page.
 *
 * Single page, modes: login | register | verify | 2fa | forgot | reset.
 * Reuses every AuthContext method (no backend changes required).
 *
 * Visual language mirrors LandingPageDark:
 *   bg-black, GradientMesh blobs (emerald/violet/blue + dot grid),
 *   glass card on backdrop-blur, mono uppercase overlines, gradient logo.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import SelectedPlanBadge from '../components/auth/SelectedPlanBadge';
import AddOnPicker from '../components/auth/AddOnPicker';
import { trackFunnel } from '../lib/funnelTracker';
import { Label } from '../components/ui/label';
import {
  ArrowRight, Sparkles, Mail, Lock, User, Eye, EyeOff,
  Loader2, CheckCircle2, AlertCircle, ArrowLeft,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { apiFetch } from '../fetchInterceptor';
import { metaInitiateCheckout } from '../lib/metaPixel';
import { Logo, useMarketingTheme, useHomepageContent } from '../components/homepage/MarketingShell';
import { Sun, Moon, Check, Star } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;


function GradientMesh() {
  return (
    <div className="pointer-events-none fixed inset-0 overflow-hidden z-0">
      <div className="absolute top-[-10%] left-[-10%] w-[600px] h-[600px] rounded-full bg-[#27B84A]/10 blur-[120px]" />
      <div className="absolute bottom-[-20%] right-[-10%] w-[700px] h-[700px] rounded-full bg-[#FF7816]/5 blur-[140px]" />
    </div>
  );
}

function Overline({ children, className }) {
  return (
    <p className={cn('text-[11px] font-mono uppercase tracking-[0.22em] text-zinc-500', className)}>
      {children}
    </p>
  );
}

function GoogleIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path fill="#EA4335" d="M12 11v3.2h8.78c-.36 2.36-2.84 6.92-8.78 6.92A9.12 9.12 0 0 1 2.88 12 9.12 9.12 0 0 1 12 2.88c2.6 0 4.36 1.12 5.36 2.08l2.6-2.5C18.32 1.04 15.56 0 12 0 5.36 0 0 5.36 0 12s5.36 12 12 12c6.92 0 11.52-4.88 11.52-11.72 0-.8-.08-1.4-.2-2H12Z" />
    </svg>
  );
}


// ─── Field primitives ──────────────────────────────────────────────────────

function GlassInput({ icon: Icon, ...props }) {
  return (
    <div className="relative">
      {Icon ? (
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9AA2AE] dark:text-zinc-500 pointer-events-none" />
      ) : null}
      <Input
        {...props}
        className={cn(
          'h-11 bg-white border border-[#E4E8E6] text-[#18202D] placeholder:text-[#9AA2AE] dark:bg-[#0D120E] dark:border-[#1C2420] dark:text-white dark:placeholder:text-zinc-600',
          'focus-visible:border-[#27B84A] focus-visible:ring-[#27B84A]/20',
          'transition-colors',
          Icon ? 'pl-10' : '',
          props.className
        )}
      />
    </div>
  );
}

function FieldLabel({ children, htmlFor }) {
  return (
    <Label htmlFor={htmlFor} className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#596273] dark:text-[#8B948F]">
      {children}
    </Label>
  );
}

function PrimaryButton({ children, ...props }) {
  return (
    <Button
      {...props}
      className={cn(
        'w-full h-11 rounded-xl bg-[#27B84A] hover:bg-[#118A35] text-white font-bold gap-2',
        'disabled:opacity-60 disabled:cursor-not-allowed',
        props.className
      )}
    >
      {children}
    </Button>
  );
}


// ─── Page ──────────────────────────────────────────────────────────────────

function WhatYouGetPanel({ smartWay, trustLine }) {
  return (
    <aside className="hidden lg:block lg:sticky lg:top-24" data-testid="register-benefits-panel">
      <div className="rounded-3xl border border-[#27B84A]/30 bg-[#F2FBF4] p-7 dark:border-[#1E3A26] dark:bg-[#0E1A11]">
        <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#118A35] dark:text-[#27B84A]">What you get</p>
        <h2 className="mt-2 text-2xl font-extrabold tracking-tight text-[#18202D] dark:text-white">Every tool. One login.</h2>
        <ul className="mt-5 space-y-3">
          {smartWay.items.map((item) => (
            <li key={item} className="flex items-center gap-2.5 text-sm text-[#18202D] dark:text-[#D9DED9]">
              <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-[#27B84A]">
                <Check className="h-3 w-3 text-white" strokeWidth={3} />
              </span>
              {item}
            </li>
          ))}
        </ul>
        <div className="mt-6 flex items-center justify-between border-t border-[#27B84A]/20 pt-4 dark:border-[#1E3A26]">
          <span className="text-sm font-bold text-[#118A35] dark:text-[#27B84A]">All included</span>
          <span className="text-lg font-extrabold text-[#18202D] dark:text-white">
            from {smartWay.total}<span className="text-xs font-semibold text-[#596273] dark:text-[#8B948F]">{smartWay.totalSuffix}</span>
          </span>
        </div>
        <div className="mt-4 flex items-center gap-2">
          <div className="flex gap-0.5" aria-label="5 star rating">
            {[...Array(5)].map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-[#F59E0B] text-[#F59E0B]" />)}
          </div>
          <p className="text-xs font-semibold text-[#596273] dark:text-[#8B948F]">{trustLine}</p>
        </div>
      </div>
    </aside>
  );
}

export default function LoginDark() {
  const { dk, toggleTheme } = useMarketingTheme();
  const homeContent = useHomepageContent();
  const {
    checkAuth,
    authError, authLoading, requires2FA, pendingEmail, clearAuthError,
    loginWithEmail, registerWithEmail, verifyRegistration,
    verify2FALogin, forgotPassword, resetPassword,
    resendVerificationCode, loginWithGoogle,
  } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const initialMode = useMemo(() => {
    const m = (searchParams.get('mode') || '').toLowerCase();
    if (m === 'register') return 'register';
    // Restore the OTP screen on refresh / from the verification email link:
    // /login?mode=verify&email=you@company.com
    if (m === 'verify' && searchParams.get('email')) return 'verify';
    // Admin-issued password-reset emails deep-link here:
    // /login?mode=reset&email=you@company.com  or  ?mode=forgot
    if (m === 'reset') return 'reset';
    if (m === 'forgot') return 'forgot';
    return 'login';
  }, [searchParams]);

  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState({
    email: searchParams.get('email') || '', password: '', name: '', code: '', newPassword: '',
  });

  // `?next=/portal` lets invite emails (e.g. Client Portal) deep-link users to
  // the right destination after they sign in.
  const nextDest = useMemo(() => {
    const n = searchParams.get('next');
    // Only allow same-origin paths starting with `/` to avoid open-redirects.
    if (n && n.startsWith('/') && !n.startsWith('//')) return n;
    return null;
  }, [searchParams]);
  const [showPwd, setShowPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [localError, setLocalError] = useState('');
  const [okMessage, setOkMessage] = useState('');
  const [devCode, setDevCode] = useState('');
  const [resendIn, setResendIn] = useState(0);

  // Resend cooldown ticker (60s after each send)
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  // ── Plan-aware register state ─────────────────────────────────────────
  // The landing page CTAs forward the picked plan via either:
  //   1. URL query: /login?mode=register&plan=pro&cadence=annual
  //   2. localStorage `plan_intent` (set on click, for cases where the URL
  //      gets stripped through OAuth redirects).
  // We pick the URL first and fall back to localStorage. Updates here are
  // mirrored back to localStorage so the post-register auto-checkout in
  // BillingSettings uses the latest selection.
  const [selectedPlan, setSelectedPlan] = useState(() => {
    const urlPlan = searchParams.get('plan');
    const urlCadence = searchParams.get('cadence');
    if (urlPlan) return { plan_id: urlPlan, cadence: urlCadence === 'annual' ? 'annual' : 'monthly', addon_ids: [] };
    try {
      const raw = window.localStorage.getItem('plan_intent');
      if (raw) {
        const i = JSON.parse(raw);
        if (i && i.plan_id && (Date.now() - (i.ts || 0)) < 10 * 60 * 1000) {
          return { plan_id: i.plan_id, cadence: i.cadence === 'annual' ? 'annual' : 'monthly', addon_ids: Array.isArray(i.addon_ids) ? i.addon_ids : [] };
        }
      }
    } catch { /* ignore */ }
    return { plan_id: 'starter', cadence: 'monthly', addon_ids: [] };
  });

  // Mirror selection to localStorage so the post-register auto-checkout fires
  // with the right plan even if the URL is dropped on a Google-OAuth bounce.
  // ONLY in register mode (or explicit ?plan=): a plain existing-user login
  // must not stage a synthetic Starter intent that hijacks post-login routing.
  useEffect(() => {
    if (mode !== 'register' && !searchParams.get('plan')) return;
    try {
      window.localStorage.setItem('plan_intent', JSON.stringify({
        plan_id: selectedPlan.plan_id, cadence: selectedPlan.cadence,
        addon_ids: selectedPlan.addon_ids || [], ts: Date.now(),
      }));
    } catch { /* ignore */ }
  }, [mode, searchParams, selectedPlan]);

  // Fire a single funnel event on first arrival to the register page so we
  // can attribute the conversion (or drop-off) to the chosen plan + cadence.
  const _initialPlanRef = useRef(null);
  useEffect(() => {
    if (mode !== 'register') return;
    if (_initialPlanRef.current === selectedPlan.plan_id + ':' + selectedPlan.cadence) return;
    const isFirst = _initialPlanRef.current === null;
    _initialPlanRef.current = selectedPlan.plan_id + ':' + selectedPlan.cadence;
    if (isFirst) {
      trackFunnel('register.plan_selected', {
        plan_id: selectedPlan.plan_id,
        cadence: selectedPlan.cadence,
      });
    } else {
      // User picked a different plan via the edit dropdown.
      trackFunnel('register.plan_changed', {
        plan_id: selectedPlan.plan_id,
        cadence: selectedPlan.cadence,
        came_from_social_proof: selectedPlan._fromSocialProof || false,
      });
    }
  }, [mode, selectedPlan]);

  const errorText = localError || authError || '';

  // Redirect if already signed in
  useEffect(() => {
    (async () => {
      const u = await checkAuth();
      if (u) navigate(nextDest || '/dashboard', { replace: true });
    })();
  }, [checkAuth, navigate, nextDest]);

  // If AuthContext signals 2FA required after a login attempt, route to 2fa
  useEffect(() => {
    if (requires2FA && pendingEmail) {
      setMode('2fa');
      setForm((f) => ({ ...f, email: pendingEmail }));
    }
  }, [requires2FA, pendingEmail]);

  const switchMode = (next) => {
    setLocalError('');
    setOkMessage('');
    setDevCode('');
    clearAuthError?.();
    setMode(next);
  };

  // ── Handlers ─────────────────────────────────────────────────────────────

  const onLogin = async (e) => {
    e.preventDefault();
    setLocalError('');
    if (!form.email || !form.password) return setLocalError('Email and password are required');
    const r = await loginWithEmail(form.email, form.password);
    if (r.success) {
      if (r.requires2FA) setMode('2fa');
      else navigate(await postAuthDest());
    } else {
      setLocalError(r.error || 'Sign-in failed');
    }
  };

  const onRegister = async (e) => {
    e.preventDefault();
    setLocalError('');
    setDevCode('');
    if (!form.email || !form.password || !form.name) return setLocalError('All fields are required');
    if (form.password.length < 8) return setLocalError('Password must be at least 8 characters');
    trackFunnel('register.submit', { plan_id: selectedPlan.plan_id, cadence: selectedPlan.cadence });
    const r = await registerWithEmail(form.email, form.password, form.name);
    if (!r.success) { setLocalError(r.error || 'Registration failed'); return; }
    // Account created + logged in (no OTP). Go straight to Stripe Checkout
    // for the 7-day trial, then Stripe returns the user to /welcome.
    trackFunnel('register.verified', { plan_id: selectedPlan.plan_id, cadence: selectedPlan.cadence });
    await startTrialCheckout();
  };

  // Opens Stripe Checkout for the selected plan (defaults to Starter monthly),
  // collecting a card for the 7-day free trial. Fires Meta InitiateCheckout.
  const startTrialCheckout = async () => {
    const planId = (selectedPlan && selectedPlan.plan_id && selectedPlan.plan_id !== 'free')
      ? selectedPlan.plan_id : 'starter';
    const cadence = (selectedPlan && selectedPlan.cadence) || 'monthly';
    setOkMessage('Opening secure checkout…');
    try {
      metaInitiateCheckout({ planId, value: planId === 'starter' ? 19.99 : undefined });
    } catch { /* ignore */ }
    try {
      const res = await apiFetch(`${API_URL}/api/billing/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan_id: planId, cadence, origin_url: window.location.origin }),
      });
      const d = await res.json();
      if (res.ok && d.url) { window.location.href = d.url; return; }
      setLocalError((d && d.detail) || 'Could not open checkout. Please try again.');
    } catch {
      setLocalError('Could not open checkout. Please try again.');
    }
  };

  const onVerifyRegistration = async (e) => {
    e.preventDefault();
    setLocalError('');
    if (!form.code || form.code.length !== 6) return setLocalError('Enter the 6-digit code');
    const r = await verifyRegistration(form.email, form.code);
    if (r.success) {
      trackFunnel('register.verified', { plan_id: selectedPlan.plan_id, cadence: selectedPlan.cadence });
      navigate(await postAuthDest());
    } else setLocalError(r.error || 'Verification failed');
  };

  const onVerify2FA = async (e) => {
    e.preventDefault();
    setLocalError('');
    if (!form.code || form.code.length !== 6) return setLocalError('Enter the 6-digit code');
    const r = await verify2FALogin(form.code);
    if (r.success) navigate(await postAuthDest());
    else setLocalError(r.error || 'Verification failed');
  };

  // Returns the post-login destination. Every new signup is funnelled
  // through Stripe Checkout — no exception. If a `plan_intent` is staged
  // (from the landing CTAs), use that; otherwise fall back to Starter
  // monthly so the card-on-file requirement applies uniformly.
  const postAuthDest = async () => {
    // Invite emails or deep-links pass `?next=`; honor that first so client
    // portal users land on /portal instead of the empty dashboard.
    if (nextDest) return nextDest;
    let intent = null;
    try {
      const raw = window.localStorage.getItem('plan_intent');
      if (raw) {
        const i = JSON.parse(raw);
        if (i && i.plan_id && (Date.now() - (i.ts || 0)) < 10 * 60 * 1000) {
          intent = { plan_id: i.plan_id, cadence: i.cadence || 'monthly' };
        }
      }
    } catch { /* ignore */ }
    // Client-portal-only users (no owner/admin/accountant role anywhere)
    // go straight to /portal. We check this ONCE here at login time — not
    // on every navigation — so owners who happen to also be a client
    // portal member somewhere don't get hijacked.
    try {
      const r = await apiFetch(`${API_URL}/api/workspaces`);
      if (r.ok) {
        const ws = await r.json();
        if (ws.length > 0 && ws.every((w) => w.role === 'client_portal')) {
          return '/portal';
        }
      }
    } catch { /* fall through */ }
    // If this is a brand-new registration (mode === 'verify' on submit),
    // ALWAYS force them through Stripe Checkout even without a plan_intent.
    // Defaults to Starter monthly. This is the gate that ensures every
    // new account has a card on file before they hit the dashboard.
    if (!intent && mode === 'verify') {
      intent = { plan_id: 'starter', cadence: 'monthly' };
    }
    if (intent) {
      return `/settings?tab=billing&auto=1&plan_intent=${intent.plan_id}&cadence=${intent.cadence}`;
    }
    return '/dashboard';
  };

  const onForgot = async (e) => {
    e.preventDefault();
    setLocalError('');
    setDevCode('');
    if (!form.email) return setLocalError('Enter your email');
    const r = await forgotPassword(form.email);
    if (r.success) {
      if (r.dev_code) setDevCode(r.dev_code);
      setMode('reset');
    } else {
      setLocalError(r.error || 'Could not send reset code');
    }
  };

  const onReset = async (e) => {
    e.preventDefault();
    setLocalError('');
    if (!form.code || form.code.length !== 6) return setLocalError('Enter the 6-digit code');
    if (!form.newPassword || form.newPassword.length < 8) return setLocalError('Password must be at least 8 characters');
    const r = await resetPassword(form.email, form.code, form.newPassword);
    if (r.success) navigate('/dashboard');
    else setLocalError(r.error || 'Reset failed');
  };

  const onResend = async () => {
    if (resendIn > 0) return;
    setLocalError('');
    const r = mode === 'reset'
      ? await forgotPassword(form.email)
      : await resendVerificationCode(form.email);
    if (r?.success) {
      if (r.dev_code) setDevCode(r.dev_code);
      setResendIn(60);
      setOkMessage('A new code has been sent to your email.');
      setTimeout(() => setOkMessage(''), 3500);
    } else {
      const msg = r?.error || 'Could not resend code';
      // Backend 429 cooldown — sync the local timer with the server's window.
      const waitMatch = /wait (\d+) second/i.exec(msg);
      if (waitMatch) setResendIn(parseInt(waitMatch[1], 10));
      setLocalError(msg);
    }
  };

  // ── Per-mode copy ───────────────────────────────────────────────────────

  // Dynamic register heading: lead with the selected plan name. Free tier
  // is suppressed (Feb 2026), so all new signups land on Starter by default.
  const _registerHeading = (() => {
    const planNames = {
      starter: 'Starter', pro: 'Pro', business: 'Business', done_for_you: 'Done For You', platinum: 'Platinum',
    };
    const planPrices = {
      starter: 19.99, pro: 49.99, business: 99.99, done_for_you: 299.99, platinum: 99.99,
    };
    const planName = planNames[selectedPlan.plan_id] || 'Starter';  // retained for analytics/debug
    const base = planPrices[selectedPlan.plan_id] ?? 19.99;
    const isAnnual = selectedPlan.cadence === 'annual';
    // Annual = true 15% off; show the discounted effective monthly rate.
    const monthly = isAnnual
      ? `$${(base * 0.85).toFixed(2)}/mo billed annually`
      : `$${base.toFixed(2)}/mo`;
    return {
      overline: 'CREATE ACCOUNT · 7-DAY FREE TRIAL',
      title: 'Start your 7-day free trial.',
      sub: `7 days free, then ${monthly}. Cancel anytime.`,
    };
  })();

  const heading = {
    login: { overline: 'SIGN IN', title: 'Welcome back.', sub: 'Enter your credentials to continue to CountBucks.' },
    register: _registerHeading,
    verify: { overline: 'EMAIL VERIFICATION', title: 'Check your inbox.', sub: `We sent a 6-digit code to ${form.email || 'your email'}.` },
    '2fa': { overline: 'TWO-FACTOR AUTH', title: 'One more step.', sub: `Enter the code sent to ${form.email || 'your email'}.` },
    forgot: { overline: 'RESET PASSWORD', title: 'Forgot your password?', sub: 'Enter your email and we will send a reset code.' },
    reset: { overline: 'NEW PASSWORD', title: 'Set a new password.', sub: `Enter the code sent to ${form.email || 'your email'}, then choose a new password.` },
  }[mode];

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div className={dk ? 'dark' : ''}>
    <div className="relative min-h-screen overflow-x-hidden bg-white text-[#18202D] dark:bg-[#0A0E0C] dark:text-[#F2F5F3]" data-testid="login-dark">
      <GradientMesh />

      {/* Slim nav */}
      <nav className="relative z-10 backdrop-blur-xl bg-white/90 border-b border-[#E4E8E6] dark:bg-[#0A0E0C]/90 dark:border-[#1C2420]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="login-logo"><Logo /></Link>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={toggleTheme}
              aria-label={dk ? 'Switch to light mode' : 'Switch to dark mode'}
              data-testid="theme-toggle"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-[#E4E8E6] text-[#596273] transition-colors hover:text-[#FF7816] dark:border-[#1C2420] dark:text-[#8B948F] dark:hover:text-[#FF7816]"
            >
              {dk ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <Link to="/" className="text-sm text-[#596273] hover:text-[#18202D] dark:text-zinc-400 dark:hover:text-white transition-colors inline-flex items-center gap-1.5" data-testid="login-back-home">
              <ArrowLeft className="w-3.5 h-3.5" />
              Home
            </Link>
          </div>
        </div>
      </nav>

      <main className="relative z-10 px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
        <div className={mode === 'register' ? 'mx-auto max-w-5xl lg:grid lg:grid-cols-[minmax(0,28rem)_minmax(0,1fr)] lg:items-start lg:gap-12' : 'max-w-md mx-auto'}>
          <div className={mode === 'register' ? 'mx-auto w-full max-w-md lg:mx-0' : ''}>
          {/* Heading */}
          <div className="text-left mb-8">
            <Overline>{heading.overline}</Overline>
            <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tighter leading-[1.1]">
              {heading.title}
            </h1>
            <p className="mt-3 text-sm text-[#596273] dark:text-[#AEB6B1] leading-relaxed">{heading.sub}</p>
          </div>

          {/* Card */}
          <div
            className="rounded-3xl border border-[#E4E8E6] bg-white p-6 sm:p-7 shadow-xl shadow-black/5 dark:border-[#1C2420] dark:bg-[#10150F]"
            data-testid="login-card"
          >
            {/* Banner messages */}
            {okMessage && (
              <div className="mb-4 p-3 rounded-lg text-sm flex items-center gap-2 bg-[#F2FBF4] text-[#118A35] border border-[#27B84A]/30 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/30">
                <CheckCircle2 className="w-4 h-4" /> {okMessage}
              </div>
            )}
            {errorText && (
              <div
                className="mb-4 p-3 rounded-lg text-sm flex items-center gap-2 bg-red-50 text-red-600 border border-red-200 dark:bg-red-500/10 dark:text-red-300 dark:border-red-500/30"
                data-testid="login-error"
              >
                <AlertCircle className="w-4 h-4" /> {errorText}
              </div>
            )}
            {devCode && (mode === 'verify' || mode === 'reset') && (
              <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-300 dark:bg-amber-500/10 dark:border-amber-500/30 text-center">
                <p className="text-amber-700 dark:text-amber-300 text-xs mb-1 font-mono uppercase tracking-wider">
                  Email delivery unavailable — dev code
                </p>
                <p className="text-2xl font-mono font-bold text-[#18202D] dark:text-white tracking-[0.4em]">{devCode}</p>
              </div>
            )}

            {/* Forms */}
            {mode === 'login' && (
              <>
                <form onSubmit={onLogin} className="space-y-4" data-testid="form-login">
                  <div className="space-y-1.5">
                    <FieldLabel htmlFor="email">Email</FieldLabel>
                    <GlassInput id="email" type="email" autoComplete="email" icon={Mail}
                      placeholder="you@company.com" value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      data-testid="login-email-input" />
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <FieldLabel htmlFor="password">Password</FieldLabel>
                      <button type="button" onClick={() => switchMode('forgot')}
                        className="text-xs text-[#596273] hover:text-[#118A35] dark:text-zinc-500 dark:hover:text-[#27B84A] transition-colors"
                        data-testid="login-forgot-link">
                        Forgot?
                      </button>
                    </div>
                    <div className="relative">
                      <GlassInput id="password" type={showPwd ? 'text' : 'password'} icon={Lock}
                        autoComplete="current-password" placeholder="Your password"
                        value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                        data-testid="login-password-input" />
                      <button type="button" onClick={() => setShowPwd((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9AA2AE] hover:text-[#596273] dark:text-zinc-500 dark:hover:text-zinc-300">
                        {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <PrimaryButton type="submit" disabled={authLoading} data-testid="login-submit-btn">
                    {authLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Sign in <ArrowRight className="w-4 h-4" />
                  </PrimaryButton>
                </form>

                <Divider label="or" />

                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-11 rounded-xl border-[#E4E8E6] bg-white hover:border-[#27B84A] hover:bg-white text-[#18202D] dark:border-[#1C2420] dark:bg-[#0D120E] dark:hover:bg-white/10 dark:text-zinc-100 gap-2"
                  onClick={loginWithGoogle}
                  data-testid="login-google-btn"
                >
                  <GoogleIcon className="w-4 h-4" />
                  Continue with Google
                </Button>

                <p className="mt-6 text-center text-sm text-[#596273] dark:text-zinc-500">
                  Don't have an account?{' '}
                  <button type="button" onClick={() => switchMode('register')}
                    className="text-[#118A35] hover:text-[#27B84A] dark:text-[#27B84A] dark:hover:text-emerald-300 font-medium"
                    data-testid="switch-to-register">
                    Sign up
                  </button>
                </p>
              </>
            )}

            {mode === 'register' && (
              <>
                <SelectedPlanBadge
                  planId={selectedPlan.plan_id}
                  cadence={selectedPlan.cadence}
                  onChange={(plan_id, cadence, opts = {}) => setSelectedPlan((prev) => ({
                    plan_id, cadence,
                    // Drop add-ons when the plan changes — they don't always
                    // apply across tiers (e.g. Tax Return is included in
                    // Platinum so the picker hides it; carrying stale ids
                    // would silently bill the user post-checkout).
                    addon_ids: prev?.plan_id === plan_id ? (prev.addon_ids || []) : [],
                    _fromSocialProof: !!opts.fromSocialProof,
                  }))}
                />
                <form onSubmit={onRegister} className="space-y-4" data-testid="form-register">
                  <div className="space-y-1.5">
                    <FieldLabel htmlFor="reg-name">Full name</FieldLabel>
                    <GlassInput id="reg-name" type="text" autoComplete="name" icon={User}
                      placeholder="Jane Doe" value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid="register-name-input" />
                  </div>
                  <div className="space-y-1.5">
                    <FieldLabel htmlFor="reg-email">Email</FieldLabel>
                    <GlassInput id="reg-email" type="email" autoComplete="email" icon={Mail}
                      placeholder="you@company.com" value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      data-testid="register-email-input" />
                  </div>
                  <div className="space-y-1.5">
                    <FieldLabel htmlFor="reg-password">Password</FieldLabel>
                    <div className="relative">
                      <GlassInput id="reg-password" type={showPwd ? 'text' : 'password'} icon={Lock}
                        autoComplete="new-password" placeholder="At least 8 characters"
                        value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                        data-testid="register-password-input" />
                      <button type="button" onClick={() => setShowPwd((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9AA2AE] hover:text-[#596273] dark:text-zinc-500 dark:hover:text-zinc-300">
                        {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="text-[11px] text-[#9AA2AE] dark:text-zinc-600">Use 8+ characters with a mix of letters & numbers.</p>
                  </div>
                  <PrimaryButton type="submit" disabled={authLoading} data-testid="register-submit-btn">
                    {authLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    <>Start 7-day free trial <ArrowRight className="w-4 h-4" /></>
                  </PrimaryButton>
                  <p className="text-[11px] text-zinc-500 text-center leading-relaxed">
                    7 days free, then $19.99/mo. Cancel before day 8 and pay $0.
                  </p>
                </form>

                {/* Optional add-ons live BELOW the button so the primary CTA
                    stays above the fold on mobile. Collapsed by default. */}
                <AddOnPicker
                  planId={selectedPlan.plan_id}
                  cadence={selectedPlan.cadence}
                  selected={selectedPlan.addon_ids || []}
                  collapsible
                  onChange={(addon_ids) => setSelectedPlan((prev) => ({ ...prev, addon_ids }))}
                />

                <Divider label="or" />

                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-11 rounded-xl border-[#E4E8E6] bg-white hover:border-[#27B84A] hover:bg-white text-[#18202D] dark:border-[#1C2420] dark:bg-[#0D120E] dark:hover:bg-white/10 dark:text-zinc-100 gap-2"
                  onClick={loginWithGoogle}
                  data-testid="register-google-btn"
                >
                  <GoogleIcon className="w-4 h-4" />
                  Continue with Google
                </Button>

                <p className="mt-6 text-center text-sm text-[#596273] dark:text-zinc-500">
                  Already have an account?{' '}
                  <button type="button" onClick={() => switchMode('login')}
                    className="text-[#118A35] hover:text-[#27B84A] dark:text-[#27B84A] dark:hover:text-emerald-300 font-medium"
                    data-testid="switch-to-login">
                    Sign in
                  </button>
                </p>
              </>
            )}

            {(mode === 'verify' || mode === '2fa') && (
              <form
                onSubmit={mode === 'verify' ? onVerifyRegistration : onVerify2FA}
                className="space-y-4"
                data-testid={`form-${mode}`}
              >
                <div className="space-y-1.5">
                  <FieldLabel htmlFor="code">6-digit code</FieldLabel>
                  <Input
                    id="code"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    className="h-14 text-center text-2xl tracking-[0.5em] font-mono bg-[#F7F9F8] border border-[#E4E8E6] text-[#18202D] placeholder:text-[#C4CBC7] focus-visible:border-[#27B84A] focus-visible:ring-[#27B84A]/20 dark:bg-zinc-900/60 dark:border-white/10 dark:text-white dark:placeholder:text-zinc-700"
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                    data-testid={`${mode}-code-input`}
                  />
                </div>
                <PrimaryButton type="submit" disabled={authLoading} data-testid={`${mode}-submit-btn`}>
                  {authLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Verify <ArrowRight className="w-4 h-4" />
                </PrimaryButton>
                <div className="text-center text-sm text-[#596273] dark:text-zinc-500">
                  Didn't get it?{' '}
                  <button type="button" onClick={onResend} disabled={resendIn > 0}
                    className="text-[#118A35] hover:text-[#27B84A] dark:text-[#27B84A] dark:hover:text-emerald-300 font-medium disabled:text-zinc-400 dark:disabled:text-zinc-600 disabled:cursor-not-allowed"
                    data-testid={`${mode}-resend`}>
                    {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend code'}
                  </button>
                </div>
                <button type="button" onClick={() => switchMode('login')}
                  className="block w-full text-center text-xs text-[#9AA2AE] hover:text-[#596273] dark:text-zinc-600 dark:hover:text-zinc-400 transition-colors"
                  data-testid={`${mode}-back-to-login`}>
                  ← Back to sign in
                </button>
              </form>
            )}

            {mode === 'forgot' && (
              <form onSubmit={onForgot} className="space-y-4" data-testid="form-forgot">
                <div className="space-y-1.5">
                  <FieldLabel htmlFor="forgot-email">Email</FieldLabel>
                  <GlassInput id="forgot-email" type="email" autoComplete="email" icon={Mail}
                    placeholder="you@company.com" value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="forgot-email-input" />
                </div>
                <PrimaryButton type="submit" disabled={authLoading} data-testid="forgot-submit-btn">
                  {authLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Send reset code <ArrowRight className="w-4 h-4" />
                </PrimaryButton>
                <button type="button" onClick={() => switchMode('login')}
                  className="block w-full text-center text-xs text-[#9AA2AE] hover:text-[#596273] dark:text-zinc-600 dark:hover:text-zinc-400 transition-colors"
                  data-testid="forgot-back-to-login">
                  ← Back to sign in
                </button>
              </form>
            )}

            {mode === 'reset' && (
              <form onSubmit={onReset} className="space-y-4" data-testid="form-reset">
                <div className="space-y-1.5">
                  <FieldLabel htmlFor="reset-code">6-digit code</FieldLabel>
                  <Input
                    id="reset-code"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    className="h-14 text-center text-2xl tracking-[0.5em] font-mono bg-[#F7F9F8] border border-[#E4E8E6] text-[#18202D] placeholder:text-[#C4CBC7] focus-visible:border-[#27B84A] focus-visible:ring-[#27B84A]/20 dark:bg-zinc-900/60 dark:border-white/10 dark:text-white dark:placeholder:text-zinc-700"
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                    data-testid="reset-code-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <FieldLabel htmlFor="new-password">New password</FieldLabel>
                  <div className="relative">
                    <GlassInput id="new-password" type={showNewPwd ? 'text' : 'password'} icon={Lock}
                      autoComplete="new-password" placeholder="At least 8 characters"
                      value={form.newPassword}
                      onChange={(e) => setForm({ ...form, newPassword: e.target.value })}
                      data-testid="reset-new-password-input" />
                    <button type="button" onClick={() => setShowNewPwd((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9AA2AE] hover:text-[#596273] dark:text-zinc-500 dark:hover:text-zinc-300">
                      {showNewPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <PrimaryButton type="submit" disabled={authLoading} data-testid="reset-submit-btn">
                  {authLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Reset password <ArrowRight className="w-4 h-4" />
                </PrimaryButton>
                <div className="text-center text-sm text-[#596273] dark:text-zinc-500">
                  Didn't get the code?{' '}
                  <button type="button" onClick={onResend} disabled={resendIn > 0}
                    className="text-[#118A35] hover:text-[#27B84A] dark:text-[#27B84A] dark:hover:text-emerald-300 font-medium disabled:text-zinc-400 dark:disabled:text-zinc-600 disabled:cursor-not-allowed"
                    data-testid="reset-resend">
                    {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend'}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Fine print */}
          <p className="mt-6 text-center text-[11px] text-[#9AA2AE] dark:text-zinc-600 font-mono uppercase tracking-wider">
            By continuing you agree to our{' '}
            <Link to="/terms" className="hover:text-[#596273] dark:hover:text-zinc-400 underline-offset-4 hover:underline">Terms</Link>{' '}
            ·{' '}
            <Link to="/privacy" className="hover:text-[#596273] dark:hover:text-zinc-400 underline-offset-4 hover:underline">Privacy</Link>
          </p>
          </div>
          {mode === 'register' && (
            <WhatYouGetPanel smartWay={homeContent.smartWay} trustLine={homeContent.hero.trustLine} />
          )}
        </div>
      </main>
    </div>
    </div>
  );
}


function Divider({ label }) {
  return (
    <div className="my-5 flex items-center gap-3" aria-hidden="true">
      <div className="flex-1 h-px bg-[#E4E8E6] dark:bg-white/10" />
      <span className="text-[10px] font-mono uppercase tracking-widest text-[#9AA2AE] dark:text-zinc-600">{label}</span>
      <div className="flex-1 h-px bg-[#E4E8E6] dark:bg-white/10" />
    </div>
  );
}
